import { useCallback, useEffect, useState } from 'react';
import { Loader, Paper, Stack, Text, Divider } from '@mantine/core';
import { Encounter, MedicationStatement, Patient } from '@medplum/fhirtypes';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';

import {
  loadPatientMedications,
  loadTherapeuticServices,
} from '../examination/examination.helpers';
import { MedicationList } from '../examination/medications/MedicationList';
import { TherapeuticServiceList } from '../examination/therapeuticServices/TherapeuticServiceList';
import { TherapeuticServiceFormData } from '../examination/therapeuticServices/TherapeuticServiceModal';

type InterventionTabProps = {
  patient: Patient;
  encounter: Encounter | undefined;
};

export const InterventionTab = ({
  patient,
  encounter,
}: InterventionTabProps) => {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages']);

  const [medications, setMedications] = useState<MedicationStatement[]>([]);
  const [services, setServices] = useState<TherapeuticServiceFormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [medicationsInputKey, setMedicationsInputKey] = useState(0);
  const [servicesInputKey, setServicesInputKey] = useState(0);

  const hasEncounterContext = Boolean(encounter?.id);

  const fetchInterventions = useCallback(async () => {
    if (!patient?.id) {
      setMedications([]);
      setServices([]);
      setMedicationsInputKey((prev) => prev + 1);
      setServicesInputKey((prev) => prev + 1);
      return;
    }

    setLoading(true);
    try {
      // Load ALL patient medications (longitudinal view)
      const medicationResult = await loadPatientMedications(medplum, patient);

      // Load all Therapeutic services
      const servicesResult = await loadTherapeuticServices(medplum, patient);

      setMedications(medicationResult);
      setServices(servicesResult);
      setMedicationsInputKey((prev) => prev + 1);
      setServicesInputKey((prev) => prev + 1);
    } catch (error) {
      console.error('Failed to load interventions', error);
      notifications.show({
        color: 'red',
        title: t('intervention.notifications.loadErrorTitle', {
          ns: 'patient-pages',
          defaultValue: 'Unable to load interventions',
        }),
        message: t('intervention.notifications.loadErrorMessage', {
          ns: 'patient-pages',
          defaultValue: 'Please try again in a few moments.',
        }),
      });
    } finally {
      setLoading(false);
    }
  }, [medplum, patient, t]);

  useEffect(() => {
    fetchInterventions();
  }, [fetchInterventions]);

  // No batch save needed - both medications and services save immediately

  if (!hasEncounterContext) {
    return (
      <Paper withBorder radius="md" p="xl">
        <Text>
          {t('examination.notifications.noEncounterMessage', {
            ns: 'patient-pages',
          })}
        </Text>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Paper withBorder radius="md" p="xl">
        <Stack align="center" gap="sm">
          <Loader />
          <Text>
            {t('intervention.loading', {
              ns: 'patient-pages',
              defaultValue: 'Loading interventions...',
            })}
          </Text>
        </Stack>
      </Paper>
    );
  }

  return (
    <Stack gap="lg">
      {/* MedicationList requires encounter to be defined.
          Since hasEncounterContext guards this render, encounter is guaranteed to exist */}
      {encounter && (
        <>
          <MedicationList
            key={`medications-${medicationsInputKey}`}
            medications={medications}
            onChange={setMedications}
            currentEncounterId={encounter.id}
            patient={patient}
            encounter={encounter}
          />

          <Divider />

          <TherapeuticServiceList
            key={`services-${servicesInputKey}`}
            services={services}
            onChange={setServices}
            patient={patient}
            encounter={encounter}
          />
        </>
      )}
    </Stack>
  );
};
